/**
 * Booking Service - Centralized API client for booking operations
 * Provides methods for fetching, creating, updating, and canceling bookings
 *
 * @module bookingService
 */

import axios from 'axios';

// API base URL - follows standard pattern from CLAUDE.md
const API_BASE = process.env.REACT_APP_API_BASE_URL || '';

/**
 * Booking Service API Client
 * All methods return promises that resolve to response data
 */
export const bookingService = {
  /**
   * Get all bookings with optional filters
   * Supports multi-tenant filtering (automatically handled by backend)
   *
   * @param {Object} filters - Optional filters
   * @param {string} filters.propertyId - Filter by specific property ID
   * @param {string} filters.status - Filter by status (upcoming, active, completed, cancelled)
   * @param {boolean} filters.financial - If true, only show completed bookings
   * @param {string} filters.checkInFrom - Filter by check-in start date (ISO string) - v1.0.19+
   * @param {string} filters.checkInTo - Filter by check-in end date (ISO string) - v1.0.19+
   * @param {string} filters.checkOutFrom - Filter by check-out start date (ISO string) - v1.0.19+
   * @param {string} filters.checkOutTo - Filter by check-out end date (ISO string) - v1.0.19+
   * @param {string} filters.dateFrom - Generic date filter for checkIn OR checkOut (ISO string) - v1.0.19+
   * @param {string} filters.dateTo - Generic date filter for checkIn OR checkOut (ISO string) - v1.0.19+
   * @param {string} filters.activeOn - Active bookings on specific date (currently occupied) (ISO string) - v1.0.19+
   * @returns {Promise<Object>} Response with bookings array and count
   *
   * @example
   * // Get all user's bookings
   * const data = await bookingService.getAll();
   *
   * @example
   * // Get bookings for specific property
   * const data = await bookingService.getAll({ propertyId: '123' });
   *
   * @example
   * // Get upcoming bookings
   * const data = await bookingService.getAll({ status: 'upcoming' });
   *
   * @example
   * // Get bookings checking in today (v1.0.19+)
   * const today = new Date();
   * today.setHours(0, 0, 0, 0);
   * const tomorrow = new Date(today);
   * tomorrow.setDate(tomorrow.getDate() + 1);
   * const data = await bookingService.getAll({
   *   checkInFrom: today.toISOString(),
   *   checkInTo: tomorrow.toISOString()
   * });
   *
   * @example
   * // Get bookings for last 30 days (v1.0.19+)
   * const thirtyDaysAgo = new Date();
   * thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
   * const data = await bookingService.getAll({
   *   dateFrom: thirtyDaysAgo.toISOString(),
   *   dateTo: new Date().toISOString()
   * });
   *
   * @example
   * // Get active bookings on specific date (v1.0.19+)
   * const data = await bookingService.getAll({
   *   activeOn: new Date('2025-01-15').toISOString()
   * });
   */
  getAll: async (filters = {}) => {
    const response = await axios.get(`${API_BASE}/api/v1/bookings`, {
      params: filters,
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`
      }
    });
    return response.data;
  },


  /**
   * Get bookings for specific property (convenience method)
   *
   * @param {string} propertyId - Property ID to fetch bookings for
   * @returns {Promise<Object>} Response with bookings array and count
   *
   * @example
   * const data = await bookingService.getByProperty('property_id_123');
   */
  getByProperty: async (propertyId) => {
    return bookingService.getAll({ propertyId });
  },

  /**
   * Get single booking by ID
   *
   * @param {string} bookingId - Booking ID to fetch
   * @returns {Promise<Object>} Response with booking object
   *
   * @example
   * const data = await bookingService.getById('booking_id_123');
   */
  getById: async (bookingId) => {
    const response = await axios.get(`${API_BASE}/api/v1/bookings/${bookingId}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`
      }
    });
    return response.data;
  },

  /**
   * Cancel a booking
   * Releases dates and updates availability in Channex
   *
   * @param {string} bookingId - Booking ID to cancel
   * @returns {Promise<Object>} Response with cancellation confirmation
   *
   * @example
   * const result = await bookingService.cancel('booking_id_123');
   */
  cancel: async (bookingId) => {
    const response = await axios.patch(
      `${API_BASE}/api/v1/bookings/${bookingId}/cancel`,
      {},
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      }
    );
    return response.data;
  },

  /**
   * Create a new booking (manual)
   *
   * @param {string} propertyId - Property ID
   * @param {Object} bookingData - Booking details
   * @returns {Promise<Object>} Response with created booking
   *
   * @example
   * const data = await bookingService.create('property_id', {
   *   checkIn: '2025-01-15',
   *   checkOut: '2025-01-20',
   *   guestName: 'John Doe',
   *   totalAmount: 1500
   * });
   */
  create: async (propertyId, bookingData) => {
    const response = await axios.post(
      `${API_BASE}/api/v1/properties/${propertyId}/bookings`,
      bookingData,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      }
    );
    return response.data;
  },

  /**
   * Update an existing booking
   *
   * @param {string} propertyId - Property ID
   * @param {string} bookingId - Booking ID to update
   * @param {Object} bookingData - Updated booking details
   * @returns {Promise<Object>} Response with updated booking
   *
   * @example
   * const data = await bookingService.update('property_id', 'booking_id', {
   *   guestName: 'Jane Doe',
   *   numberOfGuests: 3
   * });
   */
  update: async (propertyId, bookingId, bookingData) => {
    const response = await axios.put(
      `${API_BASE}/api/v1/properties/${propertyId}/bookings/${bookingId}`,
      bookingData,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      }
    );
    return response.data;
  }
};

export default bookingService;
